package com.eladfinish.bible.contest.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import com.eladfinish.bible.contest.di.getKoinInstance
import com.eladfinish.bible.contest.screens.browse.usecases.PrepareHtmlForPdfUseCase
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.events.Event

/**
 * There's no DOM/iframe interop wired into the Canvas/Skia-based Compose-for-Web renderer, so
 * this appends a *real* `<iframe>` as a sibling of the canvas (a child of `#compose-container`,
 * which is already `position: relative` — see index.html — making it the positioning context)
 * and keeps it pinned to this Box's on-screen bounds via [onGloballyPositioned].
 *
 * [htmlContent] is passed through [PrepareHtmlForPdfUseCase] before use: the raw table HTML
 * links a relative `webview/my_style.css` and references a relative `../fonts/...ttf` from
 * inside that CSS, resolved via Android's `loadDataWithBaseURL` — an iframe's `srcdoc` has no
 * matching base to resolve either against. `PrepareHtmlForPdfUseCase` was already solving the
 * exact same problem for the PDF conversion path (font embedded as base64, styles inlined), so
 * reusing it here means the preview needs no separate CSS-resolution logic of its own.
 */
@Composable
actual fun WebView(
    baseUrl: String,
    htmlContent: String,
    additionalUserAgent: String?,
    modifier: Modifier,
    isLoading: (isLoading: Boolean) -> Unit,
    onUrlClicked: ((String?) -> Unit)?,
    onCurrentPageChanged: ((String?) -> Unit)?,
    onRootPageBackClicked: (() -> Unit)?,
    isZoomEnabled: Boolean,
    bridge: WebViewBridge?,
    isPageUrl: (String) -> Boolean,
    bottomInsetCssPx: Float,
) {
    val prepareHtmlForPdfUseCase: PrepareHtmlForPdfUseCase = remember { getKoinInstance() }

    val iframe = remember {
        (document.createElement("iframe") as HTMLIFrameElement).apply {
            style.position = "absolute"
            style.border = "0"
            style.backgroundColor = "white"
            setAttribute("sandbox", "allow-same-origin")
        }
    }

    DisposableEffect(Unit) {
        document.getElementById("compose-container")?.appendChild(iframe)
        onDispose { iframe.remove() }
    }

    DisposableEffect(htmlContent) {
        isLoading(true)
        val onLoad: (Event) -> Unit = { isLoading(false) }
        iframe.addEventListener(type = "load", callback = onLoad)
        iframe.srcdoc = prepareHtmlForPdfUseCase(htmlContent)
        onDispose { iframe.removeEventListener(type = "load", callback = onLoad) }
    }

    Box(
        modifier = modifier.onGloballyPositioned { coordinates ->
            // positionInRoot()/size are in device pixels (Compose's canvas backing-store space),
            // while the iframe's CSS left/top/width/height are in CSS pixels — on a HiDPI
            // display (devicePixelRatio != 1) those differ, so without this division the iframe
            // renders devicePixelRatio-times too large and spills outside the app's own column.
            val scale = window.devicePixelRatio
            val position = coordinates.positionInRoot()
            val size = coordinates.size
            iframe.style.left = "${position.x / scale}px"
            iframe.style.top = "${position.y / scale}px"
            iframe.style.width = "${size.width / scale}px"
            iframe.style.height = "${size.height / scale}px"
        },
    )
}
